/* @jsx h */
import { Layout } from "@app/vue/components/index";
import { GroupShell } from "@app/vue/components/GroupShell";
import {
  CheckoutProvider,
  getSessionRouteRedirect,
  isTeamLinkRoute,
  isGroupSession,
  NativeBridgeProvider,
  NavStateProvider,
  NetworkStatusProvider,
  PosAuthProvider,
  RootSessionProvider,
  SheetProvider,
  ShopConnectionProvider,
  ToastProvider,
  TrainingProvider,
  TrpcProvider,
  UpdatesProvider,
  useRootSession,
} from "@app/vue/providers/index";
import { GroupAuthProvider } from "@app/vue/providers/GroupAuth";
import {
  defineComponent,
  h,
  onErrorCaptured,
  onMounted,
  onUnmounted,
  ref,
} from "vue";
import { RouterView, useRoute } from "vue-router";
import { LocalizationProvider } from "@lemmonhq/uikit";
import deLangURL from "@lemmonhq/assets/translations/de.json?url";
import enLangURL from "@lemmonhq/assets/translations/en.json?url";
import frLangURL from "@lemmonhq/assets/translations/fr.json?url";
import itLangURL from "@lemmonhq/assets/translations/it.json?url";
import kaLangURL from "@lemmonhq/assets/translations/ka.json?url";

const SessionShell = defineComponent({
  name: "SessionShell",
  setup() {
    const root = useRootSession();
    const route = useRoute();

    return () => {
      const session = root.session.value;
      // The provider starts the redirect, while this render guard prevents a
      // route from the other shell mounting for even a single tick.
      // Personal staff links render on their own, outside the POS/group shells.
      if (isTeamLinkRoute(route.path)) return <RouterView />;
      if (getSessionRouteRedirect(session, route.path)) return null;
      if (isGroupSession(session)) {
        return (
          <GroupAuthProvider session={session}>
            <GroupShell />
          </GroupAuthProvider>
        );
      }

      return (
        <NavStateProvider>
          <NativeBridgeProvider>
            <SheetProvider>
              <UpdatesProvider>
                <PosAuthProvider session={session}>
                  <TrainingProvider>
                    <ShopConnectionProvider>
                      <CheckoutProvider>
                        <Layout />
                      </CheckoutProvider>
                    </ShopConnectionProvider>
                  </TrainingProvider>
                </PosAuthProvider>
              </UpdatesProvider>
            </SheetProvider>
          </NativeBridgeProvider>
        </NavStateProvider>
      );
    };
  },
});

export const App = defineComponent({
  name: "App",
  setup() {
    const err = ref<null | Error>(null);
    onErrorCaptured((error) => {
      console.log("e", error);
      err.value = error;
    });
    const onVisibilityChange = () => {
      console.log("visibilitychange", JSON.stringify(document.visibilityState));
    };
    const onFocus = () => console.log("window focus");
    const onBlur = () => console.log("window blur");
    onMounted(() => {
      document.addEventListener("visibilitychange", onVisibilityChange);
      window.addEventListener("focus", onFocus);
      window.addEventListener("blur", onBlur);
    });
    onUnmounted(() => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
    });

    return () => {
      if (err.value) {
        return (
          <div class="h-screen w-screen flex flex-col gap-4 items-center justify-center px-6">
            <div class="text-4xl font-hero text-center">
              Uh-oh! Something went wrong!
            </div>
            <button
              onClick={(event) => {
                event.preventDefault();
                window.location.reload();
              }}
              class="btn btn-primary btn-lg"
            >
              reload
            </button>
          </div>
        );
      }

      return (
        <LocalizationProvider
          files={{
            de: deLangURL,
            en: enLangURL,
            fr: frLangURL,
            it: itLangURL,
            ka: kaLangURL,
          }}
        >
          <NetworkStatusProvider>
            <ToastProvider>
              <TrpcProvider>
                <RootSessionProvider>
                  <SessionShell />
                </RootSessionProvider>
              </TrpcProvider>
            </ToastProvider>
          </NetworkStatusProvider>
        </LocalizationProvider>
      );
    };
  },
});
